import { useState } from "react";
import { ThumbsUp, MessageSquare, Send, X } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { useLang } from "../lib/i18n";
import { Input } from "./ui/input";
import { Button } from "./ui/button";

export function ReactionSummary({ reactions, testId }) {
  const { t } = useLang();
  if (!reactions) return null;
  const hasAny = reactions.thumbs > 0 || reactions.comments.length > 0;
  return (
    <div className="mt-2 space-y-1.5" data-testid={testId}>
      {!hasAny && <div className="text-xs text-muted-foreground">{t("noReactions")}</div>}
      {reactions.thumbs > 0 && (
        <div className="inline-flex items-center gap-1.5 text-xs bg-[#DBEAFE] text-[#1E3A8A] border border-[#BFDBFE] rounded-full px-2 py-0.5">
          <ThumbsUp className="h-3 w-3" /> {reactions.thumbs} · {reactions.thumbs_by.join(", ")}
        </div>
      )}
      {reactions.comments.map((c) => (
        <div key={c.id} className="text-sm bg-secondary/60 rounded-md px-2.5 py-1.5" data-testid={`reaction-comment-${c.id}`}>
          <span className="font-medium">{c.actor_name}</span> <span className="text-muted-foreground">· {c.created_at.slice(0, 10)}</span>
          <div className="text-foreground">{c.comment}</div>
        </div>
      ))}
    </div>
  );
}

export default function UpdateReactions({ update, canReact, onChanged, idSuffix }) {
  const { t } = useLang();
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [reactions, setReactions] = useState(update.reactions);
  const sfx = idSuffix || update.id;

  const post = async (payload) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/updates/${update.id}/reactions`, payload);
      setReactions(data.reactions);
      onChanged?.();
      return true;
    } catch (e) { toast.error(apiError(e)); return false; } finally { setBusy(false); }
  };
  const removeComment = async (rid) => {
    try {
      const { data } = await api.delete(`/updates/${update.id}/reactions/${rid}`);
      setReactions(data.reactions); onChanged?.();
    } catch (e) { toast.error(apiError(e)); }
  };
  const sendComment = async () => {
    if (!comment.trim()) return;
    if (await post({ type: "COMMENT", comment })) { setComment(""); toast.success(t("commentSent")); }
  };
  if (!reactions) return null;

  return (
    <div className="mt-2 space-y-2" data-testid={`update-reactions-${sfx}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <button
          type="button"
          data-testid={`react-thumbs-${sfx}`}
          disabled={!canReact || busy}
          onClick={() => post({ type: "THUMBS_UP" })}
          className={`inline-flex items-center gap-1.5 text-xs rounded-full border px-2.5 py-1 transition-colors ${
            reactions.my_thumbs ? "bg-[#1E3A8A] text-white border-[#1E3A8A]" : "bg-white text-foreground border-border hover:bg-secondary"
          } disabled:opacity-60`}
          title={reactions.thumbs_by.join(", ")}
        >
          <ThumbsUp className="h-3.5 w-3.5" /> {reactions.thumbs}
        </button>
        <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
          <MessageSquare className="h-3.5 w-3.5" /> {reactions.comments.length}
        </span>
      </div>
      {reactions.comments.map((c) => (
        <div key={c.id} className="text-sm bg-secondary/60 rounded-md px-2.5 py-1.5 flex items-start gap-2" data-testid={`reaction-comment-${c.id}`}>
          <div className="flex-1 min-w-0">
            <span className="font-medium">{c.actor_name}</span> <span className="text-muted-foreground text-xs">· {c.created_at.slice(11, 16)}</span>
            <div>{c.comment}</div>
          </div>
          {c.mine && (
            <button type="button" className="text-muted-foreground hover:text-foreground" data-testid={`reaction-comment-remove-${c.id}`} onClick={() => removeComment(c.id)}>
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      ))}
      {canReact && (
        <div className="flex items-center gap-2">
          <Input
            data-testid={`react-comment-input-${sfx}`}
            className="h-8 text-sm"
            placeholder={t("addComment")}
            value={comment}
            maxLength={500}
            onChange={(e) => setComment(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); sendComment(); } }}
          />
          <Button size="sm" variant="outline" className="h-8" data-testid={`react-comment-send-${sfx}`} disabled={busy || !comment.trim()} onClick={sendComment}>
            <Send className="h-3.5 w-3.5" />
          </Button>
        </div>
      )}
    </div>
  );
}
